// react
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
// node-modules
import axios, { AxiosResponse } from "axios";
// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  useTheme,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { formatFileSize } from "../../utils/file";
import {
  dashboard,
  dashboardDark,
  design3D,
  design3DDark,
} from "../../../images/icons/icon";
import { EFileImportFrom } from "../../../common/src/modules";
// src

type Props = {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  fileInfo?: any;
  setFileData?: Dispatch<SetStateAction<any>>;
  setFileNameSelected?: Dispatch<SetStateAction<string>>;
  setFileInfoSelected?: Dispatch<SetStateAction<FileInfoSelectedFromGroupware>>;
  fileInfoFrom?: EFileImportFrom;
  loadingFileGroupware?: boolean;
};

interface FileData {
  id: number;
  name: string;
  type: string;
  size: string;
}

interface FileInfoSelectedFromGroupware {
  name: string;
  size: number;
  bucketId: string;
  nodeId: string;
  key: string;
}

const ImportFileGroupWare = (props: Props) => {
  const { t } = useTranslation();

  const handleClose = () => {
    props.setOpenDialog(false);
  };

  const typeFileInfo = (nameFile?: string): string => {
    if (nameFile && nameFile !== "") {
      const parts = nameFile.split(".");
      return parts[parts.length - 1];
    } else return "";
  };

  const getFileNameWithoutExtension = (nameFile?: string): string => {
    if (nameFile && nameFile !== "") {
      const lastDotIndex = nameFile.lastIndexOf(".");
      if (lastDotIndex > 0) {
        return nameFile.substring(0, lastDotIndex);
      }
      return nameFile;
    } else return "";
  };

  const selectFileHandler = async (
    bucketId: string,
    nodeId: string,
    key: string,
    name: string,
    size: number,
  ) => {
    try {
      props.setFileInfoSelected?.({
        name,
        size,
        bucketId,
        nodeId,
        key,
      });

      props.setOpenDialog(false);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const title =
    props.fileInfoFrom === EFileImportFrom.DASHBOARD
      ? t("TITLE_IMPORT_FILE_FROM_DASHBOARD")
      : t("TITLE_IMPORT_FILE_FROM_DESIGN3D");

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const CustomTableCell = styled(TableCell)({
    color: theme.palette.base.contrast,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: "12px 16px",
    fontSize: "14px",
  });

  const HeaderTableCell = styled(TableCell)({
    color: theme.palette.base.contrast,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: "12px 16px",
    fontSize: "14px",
    fontWeight: "bold",
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    zIndex: 100,
    position: "sticky",
    top: 0,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  });

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  }));

  const Icon_Design3D =
    theme.palette.mode === "light" ? design3D : design3DDark;
  const Icon_Dashboard =
    theme.palette.mode === "light" ? dashboard : dashboardDark;

  const IconDialog =
    props.fileInfoFrom === EFileImportFrom.DASHBOARD
      ? Icon_Dashboard
      : Icon_Design3D;

  return (
    <Dialog
      open={props.openDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "del-dialog8",
        style: {
          background: customModalColor.color,
          minWidth: "800px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          height: "auto",
          minHeight: "300px",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: customDialogBackgroundColor.color,
          },
        },
      }}
    >
      <DialogTitle
        style={customColor}
        className="title-name"
        id="alert-dialog-title"
      >
        <div style={{ display: "flex" }}>
          <div
            style={{
              cursor: "pointer",
              marginRight: 10,
              width: 40,
              height: 40,
              marginBottom: 12,
              backgroundImage: `url(${IconDialog})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "translateZ(0)",
              WebkitTransform: "translateZ(0)",
            }}
            role="button"
            tabIndex={0}
            aria-label="Upload from GroupWare"
          />
          <div style={{ marginTop: 5 }}>{title}</div>
          <div style={{ marginTop: 10 }}>
            <Button
              style={customColor}
              className="x-btn btn-remove"
              onClick={handleClose}
            >
              ×
            </Button>
          </div>
        </div>

        <div className="border-comm" />
      </DialogTitle>
      {props.loadingFileGroupware ? (
        <div style={{ minHeight: "200px", position: "relative" }}>
          <CircularProgress
            sx={{ position: "absolute", left: "50%", top: "50%" }}
          />
        </div>
      ) : (
        <DialogContent
          dividers
          sx={{
            padding: 0,
            borderTop: 0,
            height: "calc(90vh - 200px)",
            minHeight: "300px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* File List Table */}
          <TableContainer
            component={Paper}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              position: "relative",
              minHeight: "200px",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <StyledTableRow>
                  <HeaderTableCell
                    component="th"
                    scope="col"
                    sx={{
                      width: "80%",
                      minWidth: 200,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t("GROUPWARE_IMPORT_TABLE_NAME")}
                  </HeaderTableCell>
                  <HeaderTableCell
                    align="center"
                    component="th"
                    scope="col"
                    sx={{ width: "10%", minWidth: 20 }}
                  >
                    {t("GROUPWARE_IMPORT_TABLE_EXTENSION")}
                  </HeaderTableCell>
                  <HeaderTableCell
                    align="right"
                    component="th"
                    scope="col"
                    sx={{ width: "10%", minWidth: 20 }}
                  >
                    {t("GROUPWARE_IMPORT_TABLE_SIZE")}
                  </HeaderTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {props.fileInfo &&
                  props.fileInfo.map((file: any) => (
                    <StyledTableRow
                      key={file.nodeId}
                      hover
                      onClick={() =>
                        selectFileHandler(
                          file.bucketId,
                          file.nodeId,
                          file.key,
                          file.name,
                          file.size,
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <CustomTableCell
                        component="th"
                        scope="row"
                        sx={{
                          maxWidth: 200,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <div
                              style={{
                                whiteSpace: "normal",
                                wordBreak: "break-all",
                              }}
                            >
                              <div style={{ opacity: 0.7, fontSize: 11 }}>
                                {t("GROUPWARE_IMPORT_TOOLTIP_FULL_NAME")}:
                              </div>
                              <div>{file.name}</div>
                              {file.path && (
                                <>
                                  <div
                                    style={{
                                      opacity: 0.7,
                                      fontSize: 11,
                                      marginTop: 4,
                                    }}
                                  >
                                    {t("GROUPWARE_IMPORT_TOOLTIP_FULL_PATH")}:
                                  </div>
                                  <div>{file.path}</div>
                                </>
                              )}
                            </div>
                          }
                        >
                          <span
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                      >
                        {getFileNameWithoutExtension(file.name)}
                        </span>
                        </Tooltip>
                      </CustomTableCell>
                      <CustomTableCell align="center">
                        {typeFileInfo(file.name)}
                      </CustomTableCell>

                      <CustomTableCell align="right">
                        {formatFileSize(file.size)}
                      </CustomTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
            {(!props.fileInfo || props.fileInfo.length === 0) && (
              <div
                style={{
                  position: "absolute",
                  top: "150px",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: theme.palette.text.secondary,
                  fontStyle: "italic",
                  fontSize: "14px",
                }}
              >
                {t("NO_DATA")}
              </div>
            )}
          </TableContainer>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ImportFileGroupWare;
